import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  addCommaSeparatedParameter,
  addTextParameter,
  auditLogPath,
  createListParameters,
  resourcePath,
  withRowId,
} from '../helper/master-data-api-helpers';
import { utils } from '../helper/utils';
import { AuditLog } from '../models/audit-log.model';
import { DownloadRequest } from '../models/download-filter.model';
import {
  CreatePartManufacturerRequest,
  PartManufacturer,
  PartManufacturerFilter,
  PartManufacturerOption,
  PartManufacturerRow,
  UpdatePartManufacturerRequest,
} from '../models/part-manufacturer.model';

@Injectable({ providedIn: 'root' })
export class PartManufacturerService {
  private readonly httpClient = inject(HttpClient);
  private readonly resource = 'part-manufacturer';

  getList(page: number, limit: number, sort?: string, filters?: PartManufacturerFilter): Observable<HttpResponse<PartManufacturerRow[]>> {
    const params = createListParameters(page, limit, sort);
    addCommaSeparatedParameter(params, 'partManufacturerCodeList', filters?.partManufacturerCodeList);
    addCommaSeparatedParameter(params, 'nameList', filters?.nameList);
    addTextParameter(params, 'searchText', filters?.searchText);

    return this.httpClient
      .get<PartManufacturer[]>(utils.GetMasterDataManagementUrl(this.resource), { params, observe: 'response' })
      .pipe(map((response) => withRowId(response, (item) => item.partManufacturerCode)));
  }

  getAll(): Observable<PartManufacturerOption[]> {
    return this.httpClient.get<PartManufacturerOption[]>(utils.GetMasterDataManagementUrl(`${this.resource}/all`));
  }

  getByCode(code: string): Observable<PartManufacturer> {
    return this.httpClient.get<PartManufacturer>(utils.GetMasterDataManagementUrl(resourcePath(this.resource, code)));
  }

  create(request: CreatePartManufacturerRequest): Observable<PartManufacturer> {
    return this.httpClient.post<PartManufacturer>(utils.GetMasterDataManagementUrl(this.resource), request);
  }

  update(code: string, request: UpdatePartManufacturerRequest): Observable<PartManufacturer> {
    return this.httpClient.put<PartManufacturer>(utils.GetMasterDataManagementUrl(resourcePath(this.resource, code)), request);
  }

  delete(code: string): Observable<void> {
    return this.httpClient.delete<void>(utils.GetMasterDataManagementUrl(resourcePath(this.resource, code)));
  }

  getAuditLogs(code: string, isSystemLog = false, page = 1, limit = 100): Observable<HttpResponse<AuditLog[]>> {
    return this.httpClient.get<AuditLog[]>(
      utils.GetMasterDataManagementUrl(auditLogPath(this.resource, [code], isSystemLog, page, limit)),
      { observe: 'response' },
    );
  }

  downloadToExcel(request: DownloadRequest): Observable<Blob> {
    return this.httpClient.post(utils.GetDownloadUrl('part-manufacturers'), request, { responseType: 'blob' });
  }
}
